import React, { useMemo, useState } from "react";
import {
  ArcadeActionButton,
  ArcadeArenaCard,
  ArcadeArenaStage,
  ArcadeBlockedSlot,
  ArcadeKeyframes,
  ArcadeLogPanel,
  ArcadeMissionPrompt,
  ArcadePlayfieldFrame,
  FlashOverlay,
  createArcadeHudStripProps,
  pointsForHit,
  rankForScore,
  timeBonus,
  useArcadeActionHotkeys,
  useArcadeAutoClock,
  useArcadeClock,
  useReducedMotion,
} from "../../../games/index.js";
import {
  INVARIANT_RELAY_LEVELS,
  createInvariantRelayState,
  currentInvariantRelayStage,
  getInvariantRelayLevel,
  invariantRelayProgress,
  legalInvariantRelayMoves,
  retroactiveFixMoveGenerated,
  stepInvariantRelay,
  type InvariantRelayMove,
  type InvariantRelayMoveKind,
  type InvariantRelayStage,
  type InvariantRelayState,
} from "../algorithms/invariantRelay.js";
import { PanelSurface, panelTokens } from "@csk/demo-ui";

const ROUND_SECONDS = 45;
const HIT_POINTS = 35;

type FlashTone = "hit" | "miss" | "clear" | null;

export function InvariantRelayPanel(): React.JSX.Element {
  const [state, setState] = useState<InvariantRelayState>(() =>
    createInvariantRelayState(),
  );
  const [combo, setCombo] = useState(0);
  const [score, setScore] = useState(0);
  const [flashTone, setFlashTone] = useState<FlashTone>(null);
  const [flashToken, setFlashToken] = useState(0);
  const clock = useArcadeClock(ROUND_SECONDS);
  const { pause: pauseClock, reset: resetClock, start: startClock } = clock;
  const reducedMotion = useReducedMotion();
  const moves = useMemo(() => legalInvariantRelayMoves(state), [state]);
  const currentStage = currentInvariantRelayStage(state);
  const currentMove = moves.at(0);
  const currentMoveColor = currentMove
    ? moveColor(currentMove.kind)
    : panelTokens.accent;
  const progress = invariantRelayProgress(state);

  const showFlash = (tone: Exclude<FlashTone, null>): void => {
    setFlashTone(tone);
    setFlashToken((token) => token + 1);
  };

  const reset = (levelId = state.levelId): void => {
    setState(createInvariantRelayState(levelId));
    setCombo(0);
    setScore(0);
    setFlashTone(null);
    setFlashToken(0);
    resetClock(ROUND_SECONDS);
    startClock();
  };

  const playMove = (move: InvariantRelayMove): void => {
    const legal = moves.some(
      (candidate) =>
        candidate.kind === move.kind && candidate.stageId === move.stageId,
    );
    if (!legal) {
      setCombo(0);
      showFlash("miss");
      return;
    }

    const next = stepInvariantRelay(state, move);
    const nextCombo = combo + 1;
    const completed = next.status === "cleared";
    setState(next);
    setCombo(nextCombo);
    setScore(
      (value) =>
        value +
        pointsForHit(nextCombo, HIT_POINTS) +
        (completed ? timeBonus(clock.remaining) : 0),
    );
    showFlash(completed ? "clear" : "hit");
  };

  const playCurrentMove = (): void => {
    if (currentMove) playMove(currentMove);
  };

  useArcadeActionHotkeys({
    primaryEnabled: state.status === "playing" && currentMove !== undefined,
    onPrimary: playCurrentMove,
    onReset: reset,
  });

  useArcadeAutoClock({
    active: state.status === "playing",
    onPause: pauseClock,
    onStart: startClock,
  });

  return (
    <PanelSurface.PanelShell
      title="Invariant Relay"
      subtitle="Carry an invariant through the lifecycle gates. A late fix cannot rewrite an earlier gate."
      accentColor={panelTokens.accent}
    >
      <ArcadeKeyframes />
      <ArcadeArenaCard glowColor={`${panelTokens.accent}33`}>
        <ArcadeArenaStage
          hudProps={createArcadeHudStripProps({
            stats: [
              {
                label: "integrity",
                value: `${state.integrity}%`,
                color: panelTokens.accent,
              },
              { label: "chain", value: `${progress}%`, color: "#93c5fd" },
              {
                label: "backdate",
                value: retroactiveFixMoveGenerated() ? "on" : "off",
                color: "#fca5a5",
              },
            ],
            score,
            rank: rankForScore(score),
            rankColor: "#fbbf24",
            scoreColor: "var(--ilg-bright)",
            borderColor: "var(--ilg-border)",
            clock,
            timerAccent: panelTokens.accent,
            timerDanger: "#f87171",
            timerTrack: "rgba(148,163,184,0.18)",
            combo,
            comboColor: panelTokens.accent,
            comboMuted: panelTokens.muted,
            reducedMotion,
          })}
          levelSelectProps={{
            levels: INVARIANT_RELAY_LEVELS,
            activeLevelId: state.levelId,
            onSelect: reset,
            onReset: () => {
              reset();
            },
            accentColor: panelTokens.accent,
            borderColor: "var(--ilg-border)",
            mutedColor: "var(--ilg-muted)",
            brightColor: "var(--ilg-bright)",
            activeBackground: "rgba(94,234,212,0.14)",
            background: "rgba(6,8,16,0.42)",
          }}
        >
          <ArcadePlayfieldFrame
            borderColor="var(--ilg-border)"
            background="rgba(6,8,16,0.78)"
            accentColor={panelTokens.accent}
            secondaryColor="#93c5fd"
            gridSize={48}
            minHeight={500}
            header={
              <div className="flex items-center justify-between gap-3">
                <PanelSurface.SectionHeading>
                  Lifecycle rail
                </PanelSurface.SectionHeading>
                {currentStage ? (
                  <PanelSurface.Badge color={panelTokens.muted}>
                    next: {moveName(currentStage.move)}
                  </PanelSurface.Badge>
                ) : null}
              </div>
            }
          >
            <div className="mb-4">
              <ArcadeMissionPrompt
                label="baton target"
                title={
                  currentStage ? currentStage.label : "Lifecycle relay complete"
                }
                detail={
                  currentStage
                    ? currentStage.effect
                    : "The chain has been carried forward in order."
                }
                color={currentMoveColor}
                hotkey={currentMove ? "Space" : undefined}
                status={
                  currentMove ? `${moveName(currentMove.kind)} gate` : "clear"
                }
              />
            </div>
            <RelayRail
              stages={getCurrentLevel(state).stages}
              state={state}
              currentStage={currentStage}
            />
            <div className="mt-5 grid gap-3 rounded-2xl border border-white/10 bg-black/35 p-3 sm:grid-cols-[1fr_160px]">
              <div>
                <PanelSurface.MicroLabel>
                  Available gate
                </PanelSurface.MicroLabel>
                <div className="mt-2 flex flex-wrap gap-2">
                  {moves.map((move) => (
                    <ArcadeActionButton
                      key={move.stageId}
                      color={moveColor(move.kind)}
                      onClick={() => {
                        playMove(move);
                      }}
                      ariaLabel={`Play ${move.label}`}
                      icon={moveIcon(move.kind)}
                    >
                      {move.label}
                    </ArcadeActionButton>
                  ))}
                  {moves.length === 0 ? (
                    <span className="rounded-lg border border-[var(--ilg-border)] bg-black/30 px-3 py-2 text-xs text-[var(--ilg-muted)]">
                      No lifecycle gate is playable.
                    </span>
                  ) : null}
                </div>
              </div>
              <ArcadeBlockedSlot
                label="Missing shortcut"
                title="Backdate fix"
                detail="No move slot exists for retroactive approval."
                color="#fca5a5"
              />
            </div>

            {flashTone ? (
              <FlashOverlay
                token={flashToken}
                color={flashColor(flashTone)}
                reducedMotion={reducedMotion}
              />
            ) : null}
          </ArcadePlayfieldFrame>

          <ArcadeLogPanel
            title="Relay log"
            event={state.lastEvent}
            borderColor="var(--ilg-border)"
            background="rgba(6,8,16,0.58)"
            textColor="var(--ilg-text)"
            badges={[
              PanelSurface.badge("establish first", panelTokens.accent),
              PanelSurface.badge("preserve next", "#93c5fd"),
              PanelSurface.badge("no backdate", "#fca5a5"),
            ]}
          >
            <PanelSurface.VerdictBanner
              tone={state.status === "stalled" ? "unsafe" : "safe"}
              title={
                state.status === "cleared"
                  ? "Lifecycle carried forward"
                  : state.status === "stalled"
                    ? "Lifecycle stalled"
                    : "Only the next lifecycle gate is playable"
              }
              body={state.brief}
            />
          </ArcadeLogPanel>
        </ArcadeArenaStage>
      </ArcadeArenaCard>
    </PanelSurface.PanelShell>
  );
}

function RelayRail({
  stages,
  state,
  currentStage,
}: {
  readonly stages: readonly InvariantRelayStage[];
  readonly state: InvariantRelayState;
  readonly currentStage: InvariantRelayStage | undefined;
}): React.JSX.Element {
  const completed = new Set(state.completedStageIds);
  return (
    <div className="grid gap-3 md:grid-cols-4">
      {stages.map((stage, index) => {
        const done = completed.has(stage.id);
        const active = currentStage?.id === stage.id;
        const color = moveColor(stage.move);
        return (
          <div
            key={stage.id}
            className={[
              "relative min-h-[178px] overflow-hidden rounded-2xl border p-3 transition",
              done
                ? "border-emerald-300/50 bg-emerald-400/12"
                : active
                  ? "border-[var(--ilg-accent)] bg-[rgba(94,234,212,0.12)] shadow-[0_0_30px_rgba(94,234,212,0.16)]"
                  : "border-white/10 bg-black/30",
            ].join(" ")}
          >
            <div
              aria-hidden
              className="absolute -right-10 -top-10 h-28 w-28 rounded-full blur-2xl"
              style={{ background: `${color}22` }}
            />
            <div className="relative flex items-center justify-between gap-3">
              <span
                className="grid h-10 w-10 place-items-center rounded-xl border font-mono text-sm font-black"
                style={{
                  background: `${color}22`,
                  borderColor: `${color}77`,
                  color,
                }}
              >
                {index + 1}
              </span>
              <span className="rounded-full border border-white/10 bg-black/25 px-2 py-1 font-mono text-[10px] uppercase text-[var(--ilg-muted)]">
                {done ? "done" : active ? "live" : "queued"}
              </span>
            </div>
            <div className="relative mt-5 text-sm font-black leading-tight text-[var(--ilg-bright)]">
              {stage.label}
            </div>
            <PanelSurface.MicroLabel style={{ marginTop: 8, position: "relative" }}>
              {moveIcon(stage.move)} {moveName(stage.move)}
            </PanelSurface.MicroLabel>
            {active ? (
              <div className="relative mt-3 rounded-lg border border-[var(--ilg-accent)]/45 bg-[rgba(94,234,212,0.1)] px-2 py-1 font-mono text-[10px] font-bold uppercase text-[var(--ilg-accent)]">
                baton here
              </div>
            ) : null}
            <div className="relative mt-4 h-2 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full"
                style={{
                  background: color,
                  width: done ? "100%" : active ? "55%" : "18%",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function getCurrentLevel(
  state: InvariantRelayState,
): ReturnType<typeof getInvariantRelayLevel> {
  return getInvariantRelayLevel(state.levelId);
}

function moveName(kind: InvariantRelayMoveKind): string {
  if (kind === "establish") return "establish";
  if (kind === "preserve") return "preserve";
  if (kind === "recover") return "recover";
  return "promote";
}

function moveIcon(kind: InvariantRelayMoveKind): string {
  if (kind === "establish") return "E";
  if (kind === "preserve") return "P";
  if (kind === "recover") return "R";
  return "G";
}

function moveColor(kind: InvariantRelayMoveKind): string {
  if (kind === "establish") return panelTokens.accent;
  if (kind === "preserve") return "#93c5fd";
  if (kind === "recover") return "#fbbf24";
  return "#34d399";
}

function flashColor(tone: Exclude<FlashTone, null>): string {
  if (tone === "hit") return "rgba(94,234,212,0.2)";
  if (tone === "clear") return "rgba(52,211,153,0.24)";
  return "rgba(248,113,113,0.22)";
}
